import React, { Fragment, useEffect } from 'react';
import Helmet from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import { compose } from 'recompose';
import gql from 'graphql-tag';

import { useSegmentClient } from '@redbubble/react-segment';
import PageSection from '@redbubble/design-system/react/PageSection';
import Box from '@redbubble/design-system/react/Box';
import Text from '@redbubble/design-system/react/Text';
import * as constants from '@redbubble/design-system/react/constants';

import { useSessionContext } from '@redbubble/boom-session-context';

import { useBrowserInfo } from '../../containers/redux/withBrowserInfo';
import withUserInfo from '../../containers/apollo/withUserInfo';
import withLocaleRedirect from '../../containers/withLocaleRedirect';
import ExploreTabs from '../ExploreTabs';
import useSendImpressions from '../../hooks/useSendImpressions';
import ArtworkMasonry, {
  fragments as masonryFragments
} from '../ArtworkMasonry';
import ArtistRecommendations from '../ArtistRecommendations';
import useRecentWorksByArtists from '../../containers/apollo/withRecentWorksByArtists';
import { useUserAccount } from '../../containers/apollo/withUserAccount';
import { clientError } from '../../lib/errorReporting';
import { EXPLORE_FOLLOWING_LABEL, analyticsPayload } from '../../lib/analytics';
import { useAnalyticsActions } from '../../containers/redux/withAnalytics';
import { fragments as workModalFragments } from '../WorkModalWithMetaData';

import messages from './messages';

const logPagination = logEvent => response => {
  const nextPage = get(response, 'data.recentWorksByArtists.page');
  const nextHasMore = get(response, 'data.recentWorksByArtists.hasMore');
  const { followFeedLoadMore, endOfFollowFeed } = analyticsPayload;
  const payload = nextHasMore ? followFeedLoadMore : endOfFollowFeed;

  logEvent({ analytics: payload(nextPage) });
};

const WORK_TYPE = 'Work';
const WORK_FRAGMENT_NAME = 'WorkFieldsForFollowingFeed';
const WORK_FRAGMENT = gql`
  fragment ${WORK_FRAGMENT_NAME} on ${WORK_TYPE} {
    workId
  }
`;

const fragments = [
  {
    type: WORK_TYPE,
    name: WORK_FRAGMENT_NAME,
    fragment: WORK_FRAGMENT
  }
];

const ExploreFollowing = ({ userInfo }) => {
  const browser = useBrowserInfo();
  const { data: userAccountData } = useUserAccount();
  const { logEvent, addImpression } = useAnalyticsActions();
  const segmentClient = useSegmentClient();

  const sessionContext = useSessionContext();
  const isBot = get(sessionContext, 'isBot', false);

  const artistIds = get(userAccountData, 'userAccount.followedIds');
  const locale = get(userInfo, 'locale', 'en');
  const isLoggedIn = get(userInfo, 'isLoggedIn', false);
  useEffect(() => {
    const { currency, country } = userInfo;
    segmentClient.page('Explore', { currency, locale, country });
  }, []);

  const logComponentInView = (componentName, loading) => () => {
    logEvent({
      analytics: analyticsPayload.componentShowedInExplorePage(
        componentName,
        loading
      )
    });
  };

  useSendImpressions();

  const searchType = 'browse';
  const {
    works,
    hasMore,
    loading,
    error,
    fetchingMore,
    refetch,
    fetchMore
  } = useRecentWorksByArtists({
    // include fragments for WorkModal so data is ready for transition
    fragments: [...masonryFragments, ...fragments, ...workModalFragments],
    locale,
    artistIds,
    searchType
  });

  const desktop = !!browser.is.large;
  const profile = desktop ? constants.DESKTOP : constants.MOBILE;
  const padding = desktop ? 2 : 1;
  const showFeed = artistIds && artistIds.length > 0;

  const pageSectionProps = {
    backgroundColor: constants.COLOR_WHITE,
    profile,
    padding,
    size: constants.MEDIUM
  };

  return (
    <Fragment>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageSection {...pageSectionProps}>
        <ExploreTabs locale={locale} currentPath="/explore/following" />
        {showFeed && (
          <Fragment>
            <Box
              display="flex"
              justifyContent="space-between"
              marginBottom={browser.is.small ? 1 : 1.5}
            >
              <Text type="display2" element="h2" block>
                <FormattedMessage {...messages.followingTitle} />
              </Text>
            </Box>
            <ArtworkMasonry
              works={works}
              loading={loading}
              error={error}
              locale={locale}
              isLoggedIn={isLoggedIn}
              canFetchMore={hasMore}
              fetchingMore={fetchingMore}
              refetch={refetch}
              logImpression={(work, rank) => {
                const impression = analyticsPayload.impression(
                  'explore-follow-feed',
                  get(work, 'workId'),
                  rank
                );

                addImpression(impression);
              }}
              fetchMore={
                fetchingMore
                  ? () => {}
                  : () =>
                      fetchMore(100)
                        .then(logPagination(logEvent))
                        .catch(clientError)
              }
              onFavorite={(favoritedWork, favorited, rank) => {
                logEvent({
                  analytics: analyticsPayload.favorite({
                    newState: favorited,
                    workId: get(favoritedWork, 'workId'),
                    rank,
                    source: 'explore-follow-feed'
                  })
                });
              }}
            />
          </Fragment>
        )}
        <ArtistRecommendations
          analyticsLabel={EXPLORE_FOLLOWING_LABEL}
          workImageWidth={500}
          limit={24}
          onContentShowed={logComponentInView(
            'explore-following-artist',
            false
          )}
          profile={profile}
          browser={browser}
          userInfo={userInfo}
          transparentCards
          urlRef={isBot ? null : 'explore-following-artist'}
          addImpression={addImpression}
        />
      </PageSection>
    </Fragment>
  );
};

export default compose(
  withUserInfo,
  withLocaleRedirect
)(ExploreFollowing);
